import { VariantProps, cva } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';
import React from 'react';

import { cn } from '../../utils';

export interface ITypographyProps extends VariantProps<typeof typographyVariants> {
  element?: 'label' | 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'li' | 'p';
  children: React.ReactNode;
  className?: string;
  asChild?: boolean;
}

export const typographyVariants = cva('scroll-m-20', {
  variants: {
    variant: {
      h600: 'text-[40px] font-semibold leading-12',
      h500: 'text-[32px] font-semibold leading-10',
      h400: 'text-2xl font-semibold leading-7',
      h300: 'text-lg font-semibold leading-6',
      h200: 'text-base font-semibold leading-5',
      h100: 'text-sm font-semibold leading-4',

      labelLargeBook: 'text-base font-medium leading-5',
      labelLargeBold: 'text-base font-semibold leading-5',

      labelMediumBook: 'text-sm leading-4.5',
      labelMediumMedium: 'text-sm font-medium leading-4.5',
      labelMediumBold: 'text-sm font-semibold leading-4.5',

      labelSmallBook: 'text-xs font-medium leading-4',
      labelSmallBold: 'text-xs font-semibold leading-4',

      labelXSmallBook: 'text-[.625rem] font-medium leading-3',
      labelXSmallBold: 'text-[.625rem] font-semibold leading-3',

      paragraphLargeBook: 'text-base leading-6',
      paragraphLargeBold: 'text-base font-bold leading-6',
      paragraphBasicBook: 'text-sm leading-5',
      paragraphBasicBold: 'text-sm font-bold leading-5',

      amountLarge: 'text-[32px] font-medium leading-10',
      amountSmall: 'text-lg font-medium leading-6',
    },
  },
  defaultVariants: {
    variant: 'paragraphBasicBook',
  },
});

export const Typography = ({
  asChild = false,
  className,
  children,
  element,
  variant,
  ...props
}: ITypographyProps) => {
  const Comp = asChild ? Slot : element || 'p';
  return (
    <Comp
      className={cn(typographyVariants({ className, variant }))}
      data-variant={variant}
      {...props}
    >
      {children}
    </Comp>
  );
};
